import type { paths } from "@/api-schema";
import type { CustomerType } from "@/api-schema-enums";
import { PathsV1CustomerUuidGetParametersQueryExtend, SubjectRoles } from "@/api-schema-enums";
import { Can } from "@/casl-provider";
import { useCustomerByUuidQuery } from "@/common/api/customer/use-customer-by-uuid.query";
import { CUSTOMER_TYPE_COLORS, getCustomerTypeLabel } from "@/common/helpers/customer-display";
import { openInNewTab } from "@/common/helpers/open-in-new-tab";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { DetailHeader } from "@/components/detail-page/detail-header";
import { DetailPageLayout } from "@/components/detail-page/detail-page-layout";
import { DetailTabs } from "@/components/detail-page/detail-tabs";
import { SystemIdTab } from "@/components/detail-page/tabs/system-id-tab";
import type { DetailTab } from "@/components/detail-page/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Badge, BadgeVariant } from "@/components/ui/badge";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import { getCustomerV1Url } from "@/modules/masterdata/customer/_helpers/get-customer-v1-url";
import { CloudUpload, ExternalLinkIcon, Fingerprint, ReceiptEuroIcon, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { CustomerDetailSkeleton } from "./_components/customer-detail-skeleton";
import { OpenPositionsTab } from "./tabs/open-positions-tab/open-positions-tab";
import { OverviewTab } from "./tabs/overview-tab/overview-tab";
import { SAP_EXPORT_TAB_ID, SapExportTab } from "./tabs/sap-export-tab/sap-export-tab";

const CUSTOMER_DETAIL_EXTENDS = [
  PathsV1CustomerUuidGetParametersQueryExtend.Addresses,
  PathsV1CustomerUuidGetParametersQueryExtend.Addresses_Address,
  PathsV1CustomerUuidGetParametersQueryExtend.CustomerIdentifiers,
  PathsV1CustomerUuidGetParametersQueryExtend.Branch,
  PathsV1CustomerUuidGetParametersQueryExtend.Branch_Address,
  PathsV1CustomerUuidGetParametersQueryExtend.Branch_Company,
  PathsV1CustomerUuidGetParametersQueryExtend.Branch_Company_CompanyIdentifiers,
  PathsV1CustomerUuidGetParametersQueryExtend.Branch_BranchIdentifiers,
  PathsV1CustomerUuidGetParametersQueryExtend.Companies,
  PathsV1CustomerUuidGetParametersQueryExtend.Companies_CompanyIdentifiers,
  PathsV1CustomerUuidGetParametersQueryExtend.SapExport,
] as const;

const CUSTOMER_DETAIL_QUERY_PARAMS = { parameters: { extend: [...CUSTOMER_DETAIL_EXTENDS] } };
const CUSTOMER_DETAIL_TRACKING_NAME = "customer";

export function CustomerDetailView() {
  const { t } = useTranslation(["masterdata", "common"]);
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid ?? "";

  return (
    <Can I="read" a="Customer">
      <UuidExistenceWrapper<
        paths["/v1/customer/{uuid}"]["get"]["parameters"]["query"],
        paths["/v1/customer/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
      >
        uuid={uuid}
        query={useCustomerByUuidQuery}
        notFoundProps={{
          title: t("masterdata:CustomerNotFound"),
          message: t("masterdata:CustomerDoesNotExist"),
          backTo: "~/masterdata/customer",
          backToLabel: t("masterdata:BackToCustomerOverview"),
        }}
      >
        <CustomerDetailContent uuid={uuid} />
      </UuidExistenceWrapper>
    </Can>
  );
}

function CustomerDetailContent({ uuid }: { uuid: string }) {
  const { t } = useTranslation(["masterdata", "common", "fia"]);
  const { data: customer, isPending, isError } = useCustomerByUuidQuery(uuid, CUSTOMER_DETAIL_QUERY_PARAMS);

  if (isPending) {
    return <CustomerDetailSkeleton />;
  }

  if (isError || !customer) {
    return (
      <CenteredLayout
        variant={CenteredLayoutVariant.Default}
        title={t("masterdata:CustomerDetails")}
        subTitle={t("masterdata:CustomerDataCouldNotBeLoaded")}
      >
        {null}
      </CenteredLayout>
    );
  }

  const v1CustomerUrl = getCustomerV1Url(customer);
  const tabs: DetailTab[] = [
    {
      id: "overview",
      label: t("common:Overview"),
      icon: UserRound,
      content: () => <OverviewTab customer={customer} />,
    },
    {
      id: "open-positions",
      label: t("fia:OpenPositions"),
      icon: ReceiptEuroIcon,
      permission: { appRole: SubjectRoles.FiaUser },
      content: () => <OpenPositionsTab customerUuid={customer.uuid} />,
    },
    {
      id: SAP_EXPORT_TAB_ID,
      label: t("masterdata:SapExport"),
      icon: CloudUpload,
      content: () => <SapExportTab customer={customer} />,
    },
    {
      id: "identifiers",
      label: t("masterdata:SystemIds"),
      icon: Fingerprint,
      content: () => (
        <SystemIdTab
          uuid={customer.uuid}
          createdAt={customer.createdAt}
          updatedAt={customer.updatedAt}
          deletedAt={customer.deletedAt}
          identifiers={(customer.customerIdentifiers ?? []).map((identifier) => ({
            id: identifier.uuid,
            label: t(`masterdata:DmsSystem${identifier.type}`),
            value: identifier.identifier,
          }))}
        />
      ),
    },
  ];

  return (
    <DetailPageLayout
      variant={CenteredLayoutVariant.Wide}
      header={
        <DetailHeader
          icon={<UserRound />}
          title={getCustomerDisplayName(customer)}
          subtitle={
            customer.email ? (
              <a href={`mailto:${customer.email}`} className="underline-offset-4 hover:underline">
                {customer.email}
              </a>
            ) : undefined
          }
          badges={
            <Badge variant={BadgeVariant.Outlined} colorVariant={CUSTOMER_TYPE_COLORS[customer.type as CustomerType]}>
              {getCustomerTypeLabel(customer.type as CustomerType)}
            </Badge>
          }
          actions={
            v1CustomerUrl
              ? [
                  {
                    id: "open-v1-customer",
                    label: t("masterdata:OpenCustomerInV1"),
                    icon: ExternalLinkIcon,
                    onClick: () => openInNewTab(v1CustomerUrl),
                  },
                ]
              : []
          }
        />
      }
    >
      <DetailTabs sections={tabs} trackingName={CUSTOMER_DETAIL_TRACKING_NAME} />
    </DetailPageLayout>
  );
}
